import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, BellRing, Mail, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
const EMAIL_LABELS = { SENT: 'Email envoyé', FAILED: 'Email en échec', PAUSED: 'Email suspendu', NO_EMAIL: 'Pas d’email', DISABLED: 'Emails désactivés' };

const ReminderHistory = ({ items }) => (
  <div className="max-h-48 overflow-y-auto space-y-2" data-testid="reminder-history">
    {items.map((r) => (
      <div key={r.id} className="text-xs border rounded-md p-2 bg-gray-50">
        <div className="flex justify-between font-medium">
          <span>{fmtDate(r.sent_at)} · {r.channel === 'AUTO' ? 'Automatique' : r.sent_by_name}</span>
          <span>{Number(r.balance).toLocaleString('fr-FR')} GNF</span>
        </div>
        {r.recipients.map((p) => (
          <div key={p.parent_id} className="text-gray-600 flex items-center gap-1 mt-1">
            <Mail className="w-3 h-3" /> {p.parent_name} — {EMAIL_LABELS[p.email_status] || p.email_status}
          </div>
        ))}
      </div>
    ))}
  </div>
);

export const InvoiceReminderDialog = ({ invoice, open, onClose, onSent }) => {
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [emailsOn, setEmailsOn] = useState(true);

  useEffect(() => {
    if (open && invoice) {
      axios.get(`${API}/invoices/${invoice.id}/reminders`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
      axios.get(`${API}/finance/reminder-settings`).then((r) => setEmailsOn(r.data.emails_enabled !== false)).catch(() => {});
    }
  }, [open, invoice]);

  if (!invoice) return null;

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/invoices/${invoice.id}/remind`);
      toast.success(data.message);
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Relance impossible');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing className="w-5 h-5 text-orange-600" /> Relancer le paiement</DialogTitle>
          <DialogDescription>
            {invoice.student_name || 'Élève'} · Facture {invoice.invoice_number} · reste {Number(invoice.balance ?? invoice.total_amount).toLocaleString('fr-FR')} GNF
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-gray-700" data-testid="reminder-channels">
          {emailsOn
            ? 'Chaque parent rattaché recevra une notification dans l’application et un email de rappel.'
            : 'Chaque parent rattaché recevra une notification dans l’application (emails désactivés dans les Paramètres de l’établissement).'}
        </p>
        {history.length > 0 && (
          <div className="flex gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="reminder-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Déjà relancée {history.length} fois — dernière relance le {fmtDate(history[0].sent_at)}. Vérifiez avant d’envoyer une nouvelle relance.</span>
          </div>
        )}
        {history.length > 0 && <ReminderHistory items={history} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reminder-cancel-btn">Annuler</Button>
          <Button onClick={send} disabled={sending} className="bg-orange-600 hover:bg-orange-700" data-testid="reminder-confirm-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-4 h-4 mr-2" />}
            {history.length > 0 ? 'Relancer quand même' : 'Envoyer la relance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceReminderDialog;
